import React, { useState, useEffect } from 'react';
import { crmFetch } from '../utils/crmFetch';

/**
 * FinanceSettings.js
 * Admin UI for global finance configuration:
 *   - Invoice Template (branding + email body)
 *   - Receipt Template (branding + email body)
 *   - Reminders (schedule + message template + manual trigger)
 *
 * Backed by /api/crm/finance-settings/* endpoints.
 */
const FinanceSettings = () => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const [activeTab, setActiveTab] = useState('invoice');
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [statusMsg, setStatusMsg] = useState('');

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await crmFetch(`${backendUrl}/api/crm/finance-settings/templates`);
      const data = await res.json();
      if (data.success) {
        setSettings(data.settings);
      } else {
        setStatusMsg('Failed to load settings');
      }
    } catch (e) {
      setStatusMsg(`Error: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSettings(); /* eslint-disable-next-line */ }, []);

  const saveSection = async (section, payload) => {
    setSaving(true);
    setStatusMsg('');
    try {
      const url = `${backendUrl}/api/crm/finance-settings/${section}`;
      const res = await crmFetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success) {
        setStatusMsg('Saved successfully');
        await fetchSettings();
      } else {
        setStatusMsg(data.detail || 'Save failed');
      }
    } catch (e) {
      setStatusMsg(`Error: ${e.message}`);
    } finally {
      setSaving(false);
      setTimeout(() => setStatusMsg(''), 3500);
    }
  };

  const resetDefaults = async () => {
    if (!window.confirm('Reset ALL invoice + receipt + reminder settings to factory defaults?')) return;
    setSaving(true);
    try {
      const res = await crmFetch(`${backendUrl}/api/crm/finance-settings/reset-defaults`, {
        method: 'POST',
      });
      const data = await res.json();
      if (data.success) {
        setStatusMsg('Defaults restored');
        await fetchSettings();
      }
    } finally {
      setSaving(false);
      setTimeout(() => setStatusMsg(''), 3500);
    }
  };

  const triggerReminders = async () => {
    setSaving(true);
    try {
      const res = await crmFetch(`${backendUrl}/api/crm/invoicing/reminders/send`, {
        method: 'POST',
      });
      const data = await res.json();
      setStatusMsg(data.success ? data.message : 'Reminder send failed');
    } catch (e) {
      setStatusMsg(`Error: ${e.message}`);
    } finally {
      setSaving(false);
      setTimeout(() => setStatusMsg(''), 3500);
    }
  };

  if (loading || !settings) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <p className="text-gray-600">Loading finance settings…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6" data-testid="finance-settings-page">
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-1">⚙️ Finance Settings</h1>
          <p className="text-gray-600">
            Customize invoice/receipt branding, email body, and reminder behavior. Applied
            on the next invoice/receipt generation.
          </p>
        </div>
        <button
          onClick={resetDefaults}
          disabled={saving}
          data-testid="finance-settings-reset-btn"
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
        >
          Reset to defaults
        </button>
      </div>

      {statusMsg && (
        <div data-testid="finance-settings-status" className="mb-4 p-3 rounded-lg bg-green-50 border border-green-200 text-green-800">
          {statusMsg}
        </div>
      )}

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <div className="flex space-x-8">
          {[
            { id: 'invoice', label: '📄 Invoice Template' },
            { id: 'receipt', label: '🧾 Receipt Template' },
            { id: 'reminders', label: '⏰ Reminders' },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              data-testid={`finance-settings-tab-${t.id}`}
              className={`pb-3 px-1 border-b-2 font-medium transition-colors ${
                activeTab === t.id
                  ? 'border-kioo-primary text-kioo-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'invoice' && (
        <InvoiceTemplateForm
          initial={settings.invoice_template}
          saving={saving}
          onSave={(payload) => saveSection('invoice-template', payload)}
        />
      )}
      {activeTab === 'receipt' && (
        <ReceiptTemplateForm
          initial={settings.receipt_template}
          saving={saving}
          onSave={(payload) => saveSection('receipt-template', payload)}
        />
      )}
      {activeTab === 'reminders' && (
        <RemindersForm
          initial={settings.reminder_settings}
          saving={saving}
          onSave={(payload) => saveSection('reminder-settings', payload)}
          onTriggerNow={triggerReminders}
        />
      )}
    </div>
  );
};

// ----- Sub-form: Invoice Template -----
const InvoiceTemplateForm = ({ initial, saving, onSave }) => {
  const [form, setForm] = useState(initial);
  useEffect(() => { setForm(initial); }, [initial]);
  const update = (k, v) => setForm({ ...form, [k]: v });
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 max-w-3xl">
      <h2 className="text-xl font-bold mb-4">Invoice Template</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ColorField label="Primary color" value={form.primary_color} onChange={(v) => update('primary_color', v)} testId="inv-primary-color" />
        <ColorField label="Secondary color" value={form.secondary_color} onChange={(v) => update('secondary_color', v)} testId="inv-secondary-color" />
      </div>
      <TextArea label="Footer line 1" value={form.footer_text} onChange={(v) => update('footer_text', v)} testId="inv-footer-text" />
      <TextArea label="Footer line 2 (tagline)" value={form.footer_tagline} onChange={(v) => update('footer_tagline', v)} testId="inv-footer-tagline" />
      <TextArea label="Payment terms" value={form.payment_terms} onChange={(v) => update('payment_terms', v)} testId="inv-payment-terms" />
      <TextField label="Signature line" value={form.signature_text} onChange={(v) => update('signature_text', v)} testId="inv-signature" />
      <hr className="my-6" />
      <h3 className="font-semibold mb-3">Invoice Email</h3>
      <TextField
        label="Email subject (use {invoice_number}, {program_name}, {ministry_name})"
        value={form.email_subject_template}
        onChange={(v) => update('email_subject_template', v)}
        testId="inv-email-subject"
      />
      <TextArea label="Email intro paragraph" value={form.email_intro} onChange={(v) => update('email_intro', v)} testId="inv-email-intro" />
      <TextArea label="Email closing paragraph" value={form.email_closing} onChange={(v) => update('email_closing', v)} testId="inv-email-closing" />
      <div className="mt-4 flex justify-end">
        <button
          onClick={() => onSave(form)}
          disabled={saving}
          data-testid="invoice-template-save-btn"
          className="px-6 py-2 bg-kioo-primary text-white rounded-lg hover:bg-kioo-secondary transition-colors disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save invoice template'}
        </button>
      </div>
    </div>
  );
};

// ----- Sub-form: Receipt Template -----
const ReceiptTemplateForm = ({ initial, saving, onSave }) => {
  const [form, setForm] = useState(initial);
  useEffect(() => { setForm(initial); }, [initial]);
  const update = (k, v) => setForm({ ...form, [k]: v });
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 max-w-3xl">
      <h2 className="text-xl font-bold mb-4">Receipt Template</h2>
      <ColorField label="Primary color" value={form.primary_color} onChange={(v) => update('primary_color', v)} testId="rcp-primary-color" />
      <TextField label="Header title" value={form.header_text} onChange={(v) => update('header_text', v)} testId="rcp-header-text" />
      <TextField label="Header subtitle" value={form.header_subtitle} onChange={(v) => update('header_subtitle', v)} testId="rcp-header-subtitle" />
      <TextArea label="Thank-you note (printed at bottom)" value={form.thank_you_note} onChange={(v) => update('thank_you_note', v)} testId="rcp-thank-you" />
      <TextArea label="Footer text" value={form.footer_text} onChange={(v) => update('footer_text', v)} testId="rcp-footer-text" />
      <hr className="my-6" />
      <h3 className="font-semibold mb-3">Receipt Email</h3>
      <TextField
        label="Email subject (use {receipt_number}, {program_name}, {ministry_name})"
        value={form.email_subject_template}
        onChange={(v) => update('email_subject_template', v)}
        testId="rcp-email-subject"
      />
      <TextArea label="Email intro paragraph" value={form.email_intro} onChange={(v) => update('email_intro', v)} testId="rcp-email-intro" />
      <TextArea label="Email closing paragraph" value={form.email_closing} onChange={(v) => update('email_closing', v)} testId="rcp-email-closing" />
      <div className="mt-4 flex justify-end">
        <button
          onClick={() => onSave(form)}
          disabled={saving}
          data-testid="receipt-template-save-btn"
          className="px-6 py-2 bg-kioo-primary text-white rounded-lg hover:bg-kioo-secondary transition-colors disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save receipt template'}
        </button>
      </div>
    </div>
  );
};

// ----- Sub-form: Reminders -----
const RemindersForm = ({ initial, saving, onSave, onTriggerNow }) => {
  const [form, setForm] = useState(initial);
  useEffect(() => { setForm(initial); }, [initial]);
  const update = (k, v) => setForm({ ...form, [k]: v });

  const updateDays = (raw) => {
    const list = raw
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length)
      .map((s) => parseInt(s, 10))
      .filter((n) => !Number.isNaN(n));
    update('days_before', list);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 max-w-3xl">
      <h2 className="text-xl font-bold mb-4">Reminder Settings</h2>

      <label className="flex items-center gap-2 mb-4">
        <input
          type="checkbox"
          checked={!!form.enabled}
          onChange={(e) => update('enabled', e.target.checked)}
          data-testid="reminder-enabled-toggle"
        />
        <span className="font-medium">Reminders enabled</span>
      </label>

      <TextField
        label="Days before due date (comma-separated, e.g. 7, 3, 1)"
        value={(form.days_before || []).join(', ')}
        onChange={updateDays}
        testId="reminder-days-before"
      />

      <TextField
        label="Email subject (use {invoice_number}, {due_date})"
        value={form.email_subject_template}
        onChange={(v) => update('email_subject_template', v)}
        testId="reminder-email-subject"
      />

      <TextArea
        label="Reminder message template (use {invoice_number}, {program_name}, {due_date}, {total})"
        value={form.message_template}
        onChange={(v) => update('message_template', v)}
        rows={6}
        testId="reminder-message-template"
      />

      <div className="mt-4 flex flex-wrap gap-3 justify-end">
        <button
          onClick={onTriggerNow}
          disabled={saving}
          data-testid="reminder-send-now-btn"
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
        >
          📧 Send reminders now
        </button>
        <button
          onClick={() => onSave(form)}
          disabled={saving}
          data-testid="reminder-save-btn"
          className="px-6 py-2 bg-kioo-primary text-white rounded-lg hover:bg-kioo-secondary transition-colors disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save reminder settings'}
        </button>
      </div>
    </div>
  );
};

// ----- Tiny inputs -----
const TextField = ({ label, value, onChange, testId }) => (
  <div className="mb-3">
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    <input
      type="text"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kioo-primary"
    />
  </div>
);

const TextArea = ({ label, value, onChange, rows = 3, testId }) => (
  <div className="mb-3">
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    <textarea
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      rows={rows}
      data-testid={testId}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kioo-primary"
    />
  </div>
);

const ColorField = ({ label, value, onChange, testId }) => (
  <div className="mb-3">
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    <div className="flex items-center gap-3">
      <input
        type="color"
        value={value || '#000000'}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`${testId}-picker`}
        className="h-10 w-14 rounded border border-gray-300 cursor-pointer"
      />
      <input
        type="text"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kioo-primary font-mono text-sm"
      />
    </div>
  </div>
);

export default FinanceSettings;
